// src/components/NotificationItem.jsx
import { useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '@/context/NotificationContext';
import { resolveNotificationRoute } from '@/utils/notificationRoutes';
import { 
  CalendarIcon, 
  CheckCircleIcon, 
  InformationCircleIcon, 
  XMarkIcon 
} from '@heroicons/react/24/outline';

// Helper for relative time
function getRelativeTime(timestamp) {
  if (!timestamp) return '';
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  if (isNaN(date.getTime())) return ''; // Safety check for invalid dates
  
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);

  if (diffInSeconds < 60) return 'Just now';
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours}h ago`;
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) return `${diffInDays}d ago`;
  
  return date.toLocaleDateString(); 
}

export default function NotificationItem({ notification }) {
  const navigate = useNavigate();
  const { markNotificationsAsRead } = useNotifications();
  const { id, title, body, timestamp, isRead, type, data } = notification || {};

  // 1. ROBUST ROUTING LOGIC
  // We use the utility if it works, otherwise we fallback to safe defaults based on data presence.
  const targetRoute = useMemo(() => {
    try {
      // A. Try the utility first
      const route = resolveNotificationRoute(notification);
      if (route && !route.includes('undefined') && !route.includes('null')) {
        return route;
      }

      // B. Fallback: Manual check to prevent "Not Found" 404s
      // Only return a route if we DEFINITELY have the ID
      if (type === 'new_message' && data?.teamId) return `/teams/${data.teamId}/messages`;
      if (type === 'chat_message' && data?.chatId) return `/messages/${data.chatId}`;
      
      return null; // No valid route found
    } catch (err) {
      console.warn('Route resolution failed:', err);
      return null;
    }
  }, [notification, type, data]);

  // 2. Styling Config
  const styleConfig = useMemo(() => {
    const distinctType = type || data?.type || '';

    switch (distinctType) {
      case 'new_message':
        return {
          icon: CalendarIcon,
          iconColor: 'text-[#5a8a1f]',
          bgColor: 'bg-[#b5f03d]/15',
          borderColor: 'border-l-[#b5f03d]',
          actionLabel: 'View Message'
        };
      case 'chat_message':
        return {
          icon: CheckCircleIcon,
          iconColor: 'text-[#5a8a1f]',
          bgColor: 'bg-[#b5f03d]/15',
          borderColor: 'border-l-[#b5f03d]',
          actionLabel: 'Open Chat'
        };
      default:
        return {
          icon: InformationCircleIcon,
          iconColor: 'text-zinc-500 dark:text-zinc-400',
          bgColor: 'bg-zinc-100 dark:bg-zinc-800',
          borderColor: 'border-l-transparent',
          actionLabel: 'View'
        };
    }
  }, [type, data]);

  // 3. Handlers
  const handleMarkAsRead = useCallback(async () => {
    if (id && isRead === false) {
      await markNotificationsAsRead([id]);
    }
  }, [id, isRead, markNotificationsAsRead]);

  // Safe Navigation Handler
  const handleNavigation = useCallback((e) => {
    if (e) e.stopPropagation();
    
    // Always mark as read on interaction
    handleMarkAsRead();

    // Only navigate if we have a valid route
    if (targetRoute) {
      navigate(targetRoute);
    } else {
      console.log("No valid route for this notification:", notification);
      // Optional: Don't do anything, or show a simpler toast "Details unavailable"
    }
  }, [targetRoute, navigate, handleMarkAsRead, notification]);

  const handleDismiss = useCallback((e) => {
    e.stopPropagation();
    handleMarkAsRead();
  }, [handleMarkAsRead]);

  const Icon = styleConfig.icon;
  const isUnread = isRead === false;

  return (
    <div
      onClick={handleNavigation}
      className={`
        relative group flex gap-3 p-4 border-b border-zinc-50 dark:border-zinc-800/50 
        transition-all duration-200 cursor-pointer 
        hover:bg-zinc-50 dark:hover:bg-white/5
        ${isUnread ? `bg-white dark:bg-zinc-900 ${styleConfig.borderColor} border-l-4` : 'bg-white/60 dark:bg-transparent border-l-4 border-l-transparent opacity-80'}
      `}
      role="button"
      tabIndex={0}
    >
      {/* Icon Column */}
      <div className="flex-shrink-0 pt-0.5">
        <div className={`h-10 w-10 rounded-full flex items-center justify-center ${styleConfig.bgColor}`}>
          <Icon className={`h-5 w-5 ${styleConfig.iconColor}`} />
        </div>
      </div>

      {/* Content Column */}
      <div className="flex-1 min-w-0">
        <div className="flex justify-between items-start mb-0.5">
          <p className={`text-sm font-medium truncate pr-2 ${isUnread ? 'text-zinc-900 dark:text-zinc-100' : 'text-zinc-600 dark:text-zinc-400'}`}>
            {title || 'New Notification'}
          </p>
          <span className="text-xs text-zinc-400 whitespace-nowrap flex-shrink-0">
            {getRelativeTime(timestamp)}
          </span>
        </div>

        <p className="text-sm text-zinc-500 dark:text-zinc-400 line-clamp-2 leading-relaxed">
          {body}
        </p>

        {/* --- INLINE QUICK ACTIONS --- */}
        {/* FIX: Removed '!isRead' check so primary actions are ALWAYS visible */}
        <div className="mt-3 flex flex-wrap gap-2">
          
          {/* Primary Action - Only show if we have a valid place to go */}
          {targetRoute && (
            <button
              onClick={handleNavigation}
              className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-zinc-100 text-zinc-900 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-700 transition-colors active:scale-95"
            >
              {styleConfig.actionLabel}
            </button>
          )}

          {/* Dismiss Button - Only useful if Unread */}
          {isUnread && (
            <button
              onClick={handleDismiss}
              className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium text-zinc-500 border border-zinc-200 hover:bg-zinc-50 hover:text-zinc-700 dark:border-zinc-700 dark:text-zinc-400 dark:hover:bg-white/5 transition-colors active:scale-95"
            >
              Dismiss
            </button>
          )}
        </div>
      </div>

      {/* Unread Indicator Dot */}
      {isUnread && (
        <span className="absolute top-4 right-2 h-2 w-2 rounded-full bg-[#b5f03d] ring-2 ring-[#b5f03d]/30" />
      )}
    </div>
  );
}